import { useMemo, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { formatUSD, formatPercent } from '@/lib/format-utils'
import { useRoasStore } from '@/stores/roas'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { LineChartIcon, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import AdChart from './ad-trend-chart'
import {
  RevenueColumn,
  InstallDateColumn,
  IndexColumn,
  InstallsColumn,
} from './roas-table-columns'
import {
  baseSortedData,
  TargetDays,
  ROASDailyDataTable,
} from './base-virtualized-table'
import { exportToCsv } from '@/lib/utils'
import { shortCampaignLabel } from '../utils'

const DayNHeader = ({
  dayIndex,
  average,
  handlerDayNClick,
  sorts,
  handleSort,
}) => {
  const sortCode = `ad_day${dayIndex}`
  const currentSort = sorts?.find((s) => s.code === sortCode)
  const isAsc = currentSort?.order === 'asc'
  const isDesc = currentSort?.order === 'desc'

  const handleSortClick = (e) => {
    e.stopPropagation()
    let newOrder
    if (!currentSort) {
      newOrder = 'desc'
    } else if (isDesc) {
      newOrder = 'asc'
    } else {
      newOrder = null
    }

    if (newOrder === null) {
      const newSorts = sorts.filter((s) => {
        if (s.code === sortCode) return false
        if (s.code.startsWith('ad_day')) return false
        return true
      })
      if (newSorts.length === 0) {
        handleSort([{ code: 'install_date', order: 'desc' }])
      } else {
        handleSort(newSorts)
      }
    } else {
      const nonDayNSorts = sorts.filter((s) => !s.code.startsWith('ad_day'))
      handleSort([{ code: sortCode, order: newOrder }, ...nonDayNSorts])
    }
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center text-base text-black gap-1">
        <button
          onClick={handleSortClick}
          className="flex items-center justify-center p-0.5 hover:bg-gray-100 rounded transition-colors"
          title={isAsc ? '升序' : isDesc ? '降序' : '默认'}
        >
          {isAsc && <ArrowUp className="w-4 h-4" />}
          {isDesc && <ArrowDown className="w-4 h-4" />}
          {!isAsc && !isDesc && (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        <div
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => handlerDayNClick(dayIndex)}
        >
          <span>D{dayIndex}</span>
          <LineChartIcon className="w-4 h-4" />
        </div>
      </div>
      {average && (
        <span className="text-right text-xs text-gray-500">{average}</span>
      )}
    </div>
  )
}
// AD cell component for displaying ECPM or Impressions
const DayAdCell = ({ dayIndex, row }) => {
  const { displayAdMode } = useRoasStore()
  const showImpressions = displayAdMode === 'impressions'

  const days = row.days || {}
  const impressionDays = row.impressionDays || {}

  let value = showImpressions
    ? impressionDays[`day${dayIndex}`]
    : (days[`day${dayIndex}`] / impressionDays[`day${dayIndex}`]) * 1000

  // Convert to number to remove leading zeros
  value = Number(value)

  // Check if data exists, not just if value is 0
  const hasData = showImpressions
    ? impressionDays[`day${dayIndex}`] !== undefined
    : days[`day${dayIndex}`] !== undefined &&
      impressionDays[`day${dayIndex}`] !== undefined

  if (!hasData || isNaN(value)) {
    return null
  }

  let diffPercent = 0
  let colorClass = ''
  if (dayIndex > 0) {
    let lastValue = showImpressions
      ? impressionDays[`day${dayIndex - 1}`]
      : (days[`day${dayIndex - 1}`] / impressionDays[`day${dayIndex - 1}`]) *
        1000

    // Convert to number to remove leading zeros
    lastValue = Number(lastValue)

    const diff = value - lastValue
    colorClass = diff > 0 ? 'text-green-600' : 'text-red-600'
    diffPercent = showImpressions ? value / lastValue : diff / lastValue
  }

  return (
    <div className={`text-right text-base text-black`}>
      {showImpressions ? value : formatUSD(value, 2, 4)}
      {diffPercent > -1 && (
        <div className={`text-xs text-base text-black ${colorClass}`}>
          {formatPercent(diffPercent, 2, 2)}
        </div>
      )}
    </div>
  )
}

const AdTable = ({
  roas,
  compareRoas,
  isTotal = false,
  simplifiedMode,
  summaryMode,
}) => {
  const [sorts, setSorts] = useState([{ code: 'install_date', order: 'desc' }])
  const [isChartModalOpen, setIsChartModalOpen] = useState(false)
  const [chartDayIndex, setChartDayIndex] = useState(null)
  const { weekFilter, displayAdMode, dayOrder, setDayOrder } = useRoasStore()

  const handleSort = useCallback(
    (newSorts) => {
      setSorts(newSorts)
      const installDateSort = newSorts.find((s) => s.code === 'install_date')
      if (installDateSort) {
        setDayOrder(installDateSort.order === 'desc' ? 'asc' : 'desc')
      }
    },
    [setDayOrder],
  )

  const handlerDayNClick = useCallback((dayIndex) => {
    setIsChartModalOpen(true)
    setChartDayIndex(dayIndex)
  }, [])

  const getDayNumericValue = useCallback(
    (row, dayIndex) => {
      const showImpressions = displayAdMode === 'impressions'
      const days = row.days || {}
      const impressionDays = row.impressionDays || {}

      const impressions = Number(impressionDays[`day${dayIndex}`])
      const revenue = Number(days[`day${dayIndex}`])

      if (showImpressions) {
        if (
          impressionDays[`day${dayIndex}`] !== undefined &&
          !isNaN(impressions)
        ) {
          return impressions
        }
        return null
      } else {
        // ECPM mode
        if (
          impressionDays[`day${dayIndex}`] !== undefined &&
          days[`day${dayIndex}`] !== undefined &&
          impressions > 0
        ) {
          return (revenue / impressions) * 1000
        }
        return null
      }
    },
    [displayAdMode],
  )

  const sortedData = useMemo(() => {
    const applySort = (data) => {
      const hasDayNSort = sorts.some((s) => s.code.startsWith('ad_day'))

      if (!hasDayNSort) {
        return baseSortedData(data, weekFilter, sorts)
      }

      return [...data].sort((a, b) => {
        for (const sortItem of sorts) {
          const { code, order } = sortItem

          if (code.startsWith('ad_day')) {
            const dayIndex = parseInt(code.replace('ad_day', ''), 10)
            const aValue = getDayNumericValue(a, dayIndex)
            const bValue = getDayNumericValue(b, dayIndex)

            if (aValue === bValue) continue

            // Put nulls at the end
            if (aValue === null) return 1
            if (bValue === null) return -1

            if (order === 'asc') {
              return aValue > bValue ? 1 : -1
            } else {
              return aValue < bValue ? 1 : -1
            }
          }

          // Fallback to basic sorting for other columns
          let aValue = a[code]
          let bValue = b[code]
          if (code === 'install_date') {
            aValue = String(aValue).replace(/-/g, '')
            bValue = String(bValue).replace(/-/g, '')
          }
          if (aValue === bValue) continue
          if (order === 'asc') {
            return aValue > bValue ? 1 : -1
          } else {
            return aValue < bValue ? 1 : -1
          }
        }
        return 0
      })
    }

    const mainSorted = applySort(roas.dailyData)

    if (
      compareRoas &&
      (Array.isArray(compareRoas)
        ? compareRoas.length > 0
        : compareRoas.dailyData)
    ) {
      const compareCampaigns = Array.isArray(compareRoas)
        ? compareRoas
        : [compareRoas]
      const compareDataMaps = compareCampaigns.map((c) => {
        if (c.isDateCompare) return null
        const map = new Map()
        c?.dailyData?.forEach((row) => {
          map.set(row.install_date, row)
        })
        return map
      })
      const sortedCompareByIndex = compareCampaigns.map((c) =>
        c.isDateCompare && c.dailyData?.length ? applySort(c.dailyData) : null,
      )

      const combined = []
      mainSorted.forEach((row, mainIndex) => {
        combined.push({
          ...row,
          type: 'main',
          rowId: `${row.install_date}_main`,
        })
        compareCampaigns.forEach((c, idx) => {
          const compareRow = c.isDateCompare
            ? (sortedCompareByIndex[idx]?.[mainIndex] ?? null)
            : (compareDataMaps[idx]?.get(row.install_date) ?? null)
          if (compareRow) {
            combined.push({
              ...compareRow,
              type: 'compare',
              rowId: c.isDateCompare
                ? `idx${mainIndex}_compare_${idx}`
                : `${row.install_date}_compare_${idx}`,
              campaignName: c.campaignName || 'Compare',
            })
            if (!c.isDateCompare && compareDataMaps[idx]) {
              compareDataMaps[idx].delete(row.install_date)
            }
          }
        })
      })

      compareDataMaps.forEach((map, idx) => {
        if (!map) return
        const leftovers = Array.from(map.values()).sort((a, b) =>
          b.install_date.localeCompare(a.install_date),
        )
        leftovers.forEach((row) => {
          const existingDateIndex = combined.findLastIndex(
            (r) => r.install_date === row.install_date,
          )
          if (existingDateIndex !== -1) {
            combined.splice(existingDateIndex + 1, 0, {
              ...row,
              type: 'compare',
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          } else {
            combined.push({
              ...row,
              type: 'compare',
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          }
        })
      })

      return combined
    }

    return mainSorted.map((row) => ({
      ...row,
      type: 'main',
      rowId: `${row.install_date}_main`,
    }))
  }, [roas.dailyData, compareRoas, weekFilter, sorts, getDayNumericValue])

  const adDays = useMemo(() => {
    let indices = []
    const maxDay = roas?.dailyData?.length ?? 7
    const dayCount = Math.max(maxDay, 7)
    const useSimplified = simplifiedMode ?? isTotal
    if (useSimplified) {
      indices = TargetDays
    } else {
      indices = Array.from({ length: Math.min(dayCount, 180) }, (_, i) => i)
    }
    return dayOrder === 'desc' ? [...indices].reverse() : indices
  }, [isTotal, simplifiedMode, roas?.dailyData?.length, dayOrder])

  const columnAverages = useMemo(() => {
    const averages = {}
    const showImpressions = displayAdMode === 'impressions'

    // Sort by date descending to get recent data
    const recentRows = [...sortedData].sort((a, b) =>
      (b.install_date || '').localeCompare(a.install_date || ''),
    )

    adDays.forEach((dayIndex) => {
      let totalRevenue = 0
      let totalImpressions = 0
      let count = 0

      for (const row of recentRows) {
        if (count >= 30) break

        const days = row.days || {}
        const impressionDays = row.impressionDays || {}

        const impressions = Number(impressionDays[`day${dayIndex}`])
        const revenue = Number(days[`day${dayIndex}`])

        if (showImpressions) {
          if (
            impressionDays[`day${dayIndex}`] !== undefined &&
            !isNaN(impressions)
          ) {
            totalImpressions += impressions
            count++
          }
        } else {
          // ECPM mode
          if (
            impressionDays[`day${dayIndex}`] !== undefined &&
            days[`day${dayIndex}`] !== undefined &&
            impressions > 0
          ) {
            totalImpressions += impressions
            totalRevenue += revenue
            count++
          }
        }
      }

      if (showImpressions) {
        averages[dayIndex] = count ? (totalImpressions / count).toFixed(0) : '-'
      } else {
        averages[dayIndex] = totalImpressions
          ? formatUSD((totalRevenue / totalImpressions) * 1000, 2, 4)
          : '-'
      }
    })
    return averages
  }, [sortedData, adDays, displayAdMode])

  const getDayValue = useCallback(
    (row, dayIndex) => {
      const showImpressions = displayAdMode === 'impressions'
      const days = row.days || {}
      const impressionDays = row.impressionDays || {}
      const impressions = impressionDays[`day${dayIndex}`]

      if (showImpressions) {
        const numValue = Number(impressions)
        return isNaN(numValue) ? '' : numValue
      }

      const revenue = days[`day${dayIndex}`]
      if (!impressions || !revenue) {
        return ''
      }

      return Number(((revenue / impressions) * 1000).toFixed(4))
    },
    [displayAdMode],
  )

  const handleExportCsv = useCallback(() => {
    if (!sortedData || sortedData.length === 0) {
      return
    }

    const toFixedNumber = (value, digits = 2) => {
      if (value === undefined || value === null) {
        return ''
      }
      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) {
        return ''
      }
      return Number(numericValue.toFixed(digits))
    }

    const filename = `roas_ad_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    const exportRows = sortedData.map((row) => {
      const exportRow = {}
      exportRow['Install Date'] = row.install_date
        ? Number(row.install_date.replaceAll('-', ''))
        : ''
      exportRow.Revenue = toFixedNumber(row.revenue)
      exportRow.Installs =
        row.installs === undefined || row.installs === null ? '' : row.installs

      adDays.forEach((dayIndex) => {
        exportRow[`Day ${dayIndex}`] = getDayValue(row, dayIndex)
      })

      return exportRow
    })

    exportToCsv(exportRows, filename)
  }, [sortedData, adDays, getDayValue])

  const columns = useMemo(() => {
    const maxDay = roas?.dailyData?.length ?? 7
    const dayCount = Math.max(maxDay, 7)

    // Helper for compare row styling
    const renderCell = (content, row) => {
      if (row.type === 'compare') {
        return <div className="text-muted-foreground italic">{content}</div>
      }
      return content
    }

    const baseColumns = [
      {
        ...IndexColumn,
        render: (value, row, rowIndex) => {
          if (row.type === 'compare') return null
          return (
            <div className="text-right text-base text-black">
              {row.index || rowIndex + 1}
            </div>
          )
        },
      },
      {
        ...InstallDateColumn(
          roas.campaignId,
          handleSort,
          sorts,
          null,
          compareRoas,
          summaryMode,
          roas.dailyData,
        ),
        render: (value, row) => {
          if (row.type === 'compare') {
            const dateLabel = row.displayDate || row.install_date || ''
            const campaignShort = row.campaignName ? shortCampaignLabel(row.campaignName) : ''
            const label = campaignShort ? `${dateLabel} · ${campaignShort}` : dateLabel
            return (
              <div className="pl-5 text-muted-foreground text-sm flex items-center h-full italic" title={row.campaignName || dateLabel}>
                {label}
              </div>
            )
          }
          return (
            <div className="relative">
              {InstallDateColumn(
                roas.campaignId,
                handleSort,
                sorts,
                null,
                compareRoas,
                summaryMode,
                roas.dailyData,
              ).render(value, row)}
            </div>
          )
        },
      },
      {
        ...RevenueColumn,
        lock: true,
        render: (value, row) =>
          renderCell(RevenueColumn.render(value, row), row),
      },
      {
        ...InstallsColumn(isTotal),
        lock: true,
        render: (value, row) =>
          renderCell(InstallsColumn(isTotal).render(value, row), row),
      },
    ]

    // Add AD day columns
    const dayColumns = adDays.map((dayIndex) => ({
      code: `ad_day${dayIndex}`,
      title: (
        <DayNHeader
          dayIndex={dayIndex}
          average={columnAverages[dayIndex]}
          handlerDayNClick={handlerDayNClick}
          sorts={sorts}
          handleSort={handleSort}
        />
      ),
      width: 100,
      align: 'right',
      render: (value, row) =>
        renderCell(<DayAdCell dayIndex={dayIndex} row={row} />, row),
    }))

    return [...baseColumns, ...dayColumns]
  }, [
    roas?.campaignId,
    roas?.dailyData?.length,
    isTotal,
    sorts,
    handleSort,
    adDays,
    sortedData.length,
    columnAverages,
    handlerDayNClick,
    compareRoas,
  ])

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mt-2">
        <Button onClick={handleExportCsv}>Export CSV</Button>
        <div className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          DN的数字是由最近的30个有效数据计算得出(原始数据计算平均)。
        </div>
        <Toggles />
      </div>
      <div className="rounded-md border" style={{ height: '600px' }}>
        <ROASDailyDataTable
          dataSource={sortedData}
          columns={columns}
          features={{ sort: { sorts, onChange: handleSort } }}
          primaryKey="rowId"
        />
      </div>
      <ChartCodeViewer
        open={isChartModalOpen}
        onOpenChange={setIsChartModalOpen}
        hideButton={true}
      >
        <AdChart
          roas={roas}
          compareRoas={compareRoas}
          dayIndex={chartDayIndex}
        />
      </ChartCodeViewer>
    </>
  )
}

const Toggles = () => {
  const { displayAdMode, setDisplayAdMode, dayOrder, setDayOrder } =
    useRoasStore()
  return (
    <div className="flex gap-2 ml-auto">
      <Button
        variant="outline"
        size="sm"
        className="h-9 bg-muted text-muted-foreground border-none hover:bg-muted/80"
        onClick={() => setDayOrder(dayOrder === 'asc' ? 'desc' : 'asc')}
      >
        {dayOrder === 'asc' ? 'D0 → DN' : 'DN → D0'}
      </Button>
      <Tabs value={displayAdMode} onValueChange={setDisplayAdMode}>
        <TabsList>
          <TabsTrigger value="ecpm">ECPM</TabsTrigger>
          <TabsTrigger value="impressions">Impressions</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  )
}

export default AdTable
